import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../app.config';
import { catchError, firstValueFrom, forkJoin, map, of, switchMap, throwError } from 'rxjs';
import { ChargeItem, ChargeItemDefinition, Invoice } from 'fhir/r4';
import { UtilityService } from '../shared/utility.service';

@Injectable({ providedIn: 'root' })
export class ChargeItemPricingService {
  private http = inject(HttpClient);
  private backendEndPoint = inject(backendEndPointToken);
  private utilityService = inject(UtilityService);

  /** Cache ChargeItemDefinition prices by canonical/id to avoid repeated GETs */
  private priceCache = new Map<string, number>();

  private extractCanonicalId(canonicalOrRef?: string | null): string | null {
    // canonical examples:
    // - "ChargeItemDefinition/{id}"
    // - "https://server/ChargeItemDefinition/{id}"
    // - "ChargeItemDefinition/{id}|{version}"
    const raw = (canonicalOrRef ?? '').toString().trim();
    if (!raw) return null;

    const noVersion = raw.split('|')[0].trim();

    const idx = noVersion.lastIndexOf('ChargeItemDefinition/');
    if (idx >= 0) return noVersion.substring(idx + 'ChargeItemDefinition/'.length) || null;

    if (noVersion.includes('/')) {
      const parts = noVersion.split('/').filter(Boolean);
      return parts.length ? (parts[parts.length - 1] || null) : null;
    }

    return noVersion;
  }

  private computeUnitPriceFromDefinition(def: ChargeItemDefinition): number {
    const result = this.utilityService.seperateTotalPriceComponents(def);
    return result ? Number(result.totalSellingPrice || 0) : 0;
  }

  /**
   * Resolve unit price for a ChargeItem by following:
   * chargeItem.definitionCanonical -> ChargeItemDefinition -> propertyGroup[0].priceComponent
   *
   * Returns a NUMBER (unit price). Currency can be handled by caller if needed.
   */
  async getUnitPriceFromChargeItemDefinitionCanonical(ci: ChargeItem): Promise<number> {
    const canonical =
      (ci as any)?.definitionCanonical ??
      (ci as any)?.definitionReference?.reference ??
      null;

    const id = this.extractCanonicalId(canonical);
    if (!id) return 0;

    const cacheKey = (canonical ?? id).toString();
    const cached = this.priceCache.get(cacheKey);
    if (cached != null) return cached;

    const def = await firstValueFrom(
      this.http.get<ChargeItemDefinition>(`${this.backendEndPoint}/ChargeItemDefinition/${id}`).pipe(
        catchError(() => of(null as any))
      )
    );

    if (!def) {
      this.priceCache.set(cacheKey, 0);
      return 0;
    }

    const price = this.computeUnitPriceFromDefinition(def);
    this.priceCache.set(cacheKey, price);
    return price;
  }

  private money(value: number, currency: string) {
    return {
      value,
      currency
    } as any;
  }

  private toNumber(x: any): number {
    const n = Number(x?.value ?? x);
    return Number.isFinite(n) ? n : 0;
  }

  private coerceCurrencyCode(input: any): string | null {
    const c = (input ?? '').toString().trim();
    if (!c) return null;
    return c.toUpperCase();
  }

  private detectInvoiceCurrency(inv: Invoice): { currency: string | null; isMixed: boolean } {
    const currencies = new Set<string>();

    const tg = this.coerceCurrencyCode((inv as any)?.totalGross?.currency);
    const tn = this.coerceCurrencyCode((inv as any)?.totalNet?.currency);
    if (tg) currencies.add(tg);
    if (tn) currencies.add(tn);

    const lineItems: any[] = Array.isArray((inv as any)?.lineItem) ? ((inv as any).lineItem as any[]) : [];
    for (const li of lineItems) {
      const pcs: any[] = Array.isArray(li?.priceComponent) ? li.priceComponent : [];
      for (const pc of pcs) {
        const c = this.coerceCurrencyCode(pc?.amount?.currency);
        if (c) currencies.add(c);
      }
    }

    if (currencies.size <= 1) {
      return { currency: currencies.size === 1 ? Array.from(currencies)[0] : null, isMixed: false };
    }
    return { currency: null, isMixed: true };
  }

  private recomputeInvoiceTotalsFromLineItems(inv: Invoice, currency: string): Invoice {
    const lineItems = (inv as any)?.lineItem ?? [];
    const componentTotals: Record<string, number> = {
      base: 0,
      surcharge: 0,
      tax: 0,
      deduction: 0,
      discount: 0,
      informational: 0
    };

    for (const li of lineItems) {
      const pcs: any[] = Array.isArray(li?.priceComponent) ? li.priceComponent : [];
      for (const pc of pcs) {
        const t = (pc?.type ?? '').toString();
        if (!t) continue;
        if (componentTotals[t] === undefined) componentTotals[t] = 0;
        componentTotals[t] += this.toNumber(pc?.amount);
      }
    }

    const totalNetValue =
      (componentTotals['base'] || 0) +
      (componentTotals['surcharge'] || 0) -
      (componentTotals['deduction'] || 0) -
      (componentTotals['discount'] || 0);

    const totalGrossValue = totalNetValue + (componentTotals['tax'] || 0);

    const totalPriceComponent = (['base', 'surcharge', 'tax', 'deduction', 'discount', 'informational'] as const)
      .map((t) => {
        const v = componentTotals[t] || 0;
        if (!Number.isFinite(v) || v === 0) return null;
        return {
          type: t,
          amount: this.money(v, currency)
        };
      })
      .filter(Boolean);

    return {
      ...(inv as any),
      totalPriceComponent: totalPriceComponent as any,
      totalNet: this.money(Math.max(0, totalNetValue), currency) as any,
      totalGross: this.money(Math.max(0, totalGrossValue), currency) as any,
    } as Invoice;
  }

  /**
   * Supplement billing when no ChargeItemDefinition exists:
   * - sets ChargeItem.priceOverride (unit price)
   * - sets Invoice.lineItem[i].priceComponent (scaled by quantity)
   * - recomputes Invoice totals IF missing/zero
   */
  applyManualPriceToChargeItemAndInvoice(params: {
    chargeItemId: string;
    invoiceId: string;
    unitPrice: number;
    currency: string;
  }, overrideReason?: string) {
    const chargeItemId = (params?.chargeItemId ?? '').toString();
    const invoiceId = (params?.invoiceId ?? '').toString();
    const unitPrice = Number(params?.unitPrice ?? 0);
    const currency = (params?.currency ?? 'NGN').toString().toUpperCase() || 'NGN';

    if (!chargeItemId || !invoiceId || !Number.isFinite(unitPrice) || unitPrice <= 0) {
      return throwError(() => new Error('Invalid manual pricing parameters.'));
    }

    const getCi$ = this.http.get<ChargeItem>(`${this.backendEndPoint}/ChargeItem/${chargeItemId}`).pipe(
      catchError((e) => throwError(() => e))
    );
    const getInv$ = this.http.get<Invoice>(`${this.backendEndPoint}/Invoice/${invoiceId}`).pipe(
      catchError((e) => throwError(() => e))
    );

    return forkJoin({ chargeItem: getCi$, invoice: getInv$ }).pipe(
      switchMap(({ chargeItem, invoice }) => {
        const invStatus = (invoice?.status ?? '').toString().toLowerCase();
        if (invStatus === 'balanced') {
          return throwError(() => new Error('Cannot override price for a paid invoice (status is balanced).'));
        }

        const detected = this.detectInvoiceCurrency(invoice);
        if (detected.isMixed) {
          return throwError(() => new Error('Invoice has mixed currencies. Cannot apply a new price until currencies are consistent.'));
        }
        if (detected.currency && detected.currency !== currency) {
          return throwError(() => new Error(`Currency mismatch: Invoice is ${detected.currency} but you selected ${currency}.`));
        }

        const qty = Number((chargeItem as any)?.quantity?.value ?? (chargeItem as any)?.quantity ?? 1);
        const quantity = Number.isFinite(qty) && qty > 0 ? qty : 1;
        const totalAmount = unitPrice * quantity;

        const updatedChargeItem: ChargeItem = {
          ...(chargeItem as any),
          priceOverride: this.money(unitPrice, currency) as any,
          overrideReason: overrideReason ?? 'No ChargeItemDefinition available; manual price applied.'
        };

        const lineItems: any[] = Array.isArray((invoice as any)?.lineItem) ? ((invoice as any).lineItem as any[]) : [];
        const liIndex = lineItems.findIndex((li: any) => {
          const ref = (li?.chargeItemReference?.reference ?? '').toString();
          return ref === `ChargeItem/${chargeItemId}` || ref.endsWith(`/ChargeItem/${chargeItemId}`) || ref.endsWith(`/${chargeItemId}`);
        });

        if (liIndex < 0) {
          return throwError(() => new Error('Could not locate matching Invoice.lineItem for this ChargeItem.'));
        }

        const nextLineItems = [...lineItems];

        // Preserve any existing informational priceComponents (e.g., cost snapshots)
        const existingLineItem = nextLineItems[liIndex] || {};
        const existingPriceComponents: any[] = Array.isArray(existingLineItem.priceComponent) ? existingLineItem.priceComponent : [];
        const informationalComponents = existingPriceComponents.filter((pc: any) => {
          const t = (pc?.type ?? '').toString().trim().toLowerCase();
          return t === 'informational';
        });

        const baseComponent = {
          type: 'base',
          amount: this.money(totalAmount, currency)
        } as any;

        // New priceComponent array: base first, then preserved informational entries
        nextLineItems[liIndex] = {
          ...nextLineItems[liIndex],
          priceComponent: [baseComponent, ...informationalComponents]
        };

        let updatedInvoice: Invoice = {
          ...(invoice as any),
          lineItem: nextLineItems as any
        };

        // Always recompute totals after override so invoice reflects the new price.
        updatedInvoice = this.recomputeInvoiceTotalsFromLineItems(updatedInvoice, currency);

        const putCi$ = this.http.put<ChargeItem>(
          `${this.backendEndPoint}/ChargeItem/${chargeItemId}`,
          updatedChargeItem,
          { headers: { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' } }
        );

        const putInv$ = this.http.put<Invoice>(
          `${this.backendEndPoint}/Invoice/${invoiceId}`,
          updatedInvoice,
          { headers: { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' } }
        );

        return forkJoin({ chargeItem: putCi$, invoice: putInv$ }).pipe(
          map(({ chargeItem: ciSaved, invoice: invSaved }) => ({
            chargeItem: ciSaved,
            invoice: invSaved,
            unitPriceValue: unitPrice,
            currency
          }))
        );
      }),
      catchError((e) => throwError(() => e))
    );
  }
}
